import React, { useState, useMemo, useEffect } from 'react';
import { Alert, AlertTitle, Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { tariffPlansResourceApi } from '../../../entities/finances/tariffPlansResource/redux/tariffPlansResource.api';
import style from './CompanyTariffPlanPanel.module.css';
import useAuth from '../../../shared/lib/hooks/useAuth';
import DateWithCheckbox from '../../../shared/ui/DateWithCheckbox/DateWithCheckbox';

const getInitialState = (acc) => ({
  selectedNextTariff: null,
  selectedCurrentTariff: null,
  nextPaymentDate: acc?.nextPlanActivateDate || '',
  stopBefore: acc?.stopBefore || null,
  stopAfter: acc?.stopAfter || null,
});

// Отображает тарифный план по компании (текущий и следующий), комобобокс и кнопки смены тарифа
function CompanyTariffPlanPanel(props) {
  const { companyAccount, putCompanyAccountParams, loading, headerPrefix, onChangeCB } = props;

  const [formState, setFormState] = useState(() => getInitialState(companyAccount));

  const [isChangeTariff, setIsChangeTariff] = useState(false);

  const { user, isDealer } = useAuth();
  const isUserTariff = companyAccount?.company?.companyId === user?.company?.companyId;

  const tariffPlans = tariffPlansResourceApi.useGetTariffPlansQuery();

  const { currentTariffPlan, nextTariffPlan } = useMemo(() => {
    if (!isDealer && (user.currentTariffPlan || user.nextTariffPlan)) {
      return {
        currentTariffPlan: user.currentTariffPlan,
        nextTariffPlan: user.nextTariffPlan,
      };
    }
    const currentTarifPlanId = companyAccount?.currentPlan?.tarifPlanId;
    const nextTariffPlanId = companyAccount?.nextPlan?.tarifPlanId;
    const currentTariff = tariffPlans?.data?.data?.find((tariff) => tariff.tarifPlanId === currentTarifPlanId) || null;
    const nextTariff = tariffPlans?.data?.data?.find((tariff) => tariff.tarifPlanId === nextTariffPlanId) || null;
    return { currentTariffPlan: currentTariff, nextTariffPlan: nextTariff };
  }, [user, companyAccount, tariffPlans]);

  const optionsTariffPlans = useMemo(() => {
    const currentTariffPlans =
      isDealer && !isUserTariff
        ? tariffPlans?.data?.data?.filter((plan) => plan?.dealerCompany?.companyId === user?.company?.companyId)
        : tariffPlans?.data?.data;

    return [...(currentTariffPlans || [])].sort((a, b) => a?.name?.localeCompare(b?.name));
  }, [tariffPlans, currentTariffPlan, isUserTariff, isDealer]);

  useEffect(() => {
    setFormState(getInitialState(companyAccount));
  }, [companyAccount]);

  useEffect(() => {
    if (optionsTariffPlans && nextTariffPlan) {
      setFormState((prev) => ({
        ...prev,
        selectedCurrentTariff: currentTariffPlan,
        selectedNextTariff: nextTariffPlan,
      }));
      setIsChangeTariff(false);
    }
  }, [optionsTariffPlans, nextTariffPlan, currentTariffPlan, companyAccount]);

  const handleFormChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setIsChangeTariff(true);
  };

  const updateTariff = () => {
    setIsChangeTariff(false);
    putCompanyAccountParams?.({
      id: companyAccount?.companyAccountId,
      body: {
        ...companyAccount,
        nextPlan: formState.selectedNextTariff ? { tarifPlanId: formState.selectedNextTariff.tarifPlanId } : null,
        stopBefore: formState?.stopBefore || null,
        stopAfter: formState?.stopAfter || null,
      },
    });
  };

  const resetSelectedTariff = () => {
    setFormState((prev) => ({
      ...prev,
      selectedNextTariff: nextTariffPlan,
    }));
    setIsChangeTariff(false);
  };

  const handleChangeNextPaymentDate = (newValue) => {
    const isoDate = newValue ? newValue.toISOString() : null;
    handleFormChange('nextPaymentDate', isoDate);
    onChangeCB?.('nextPlanActivateDate', isoDate);
  };

  const isErr = !loading && !companyAccount;

  return (
    <Box className={style.companyTariffPlanPanel}>
      {onChangeCB ? (
        <Autocomplete
          sx={{
            width: onChangeCB && putCompanyAccountParams ? '100%' : '400px',
          }}
          size="small"
          value={formState.selectedCurrentTariff}
          onChange={(_, newValue) => {
            handleFormChange('selectedCurrentTariff', newValue);
            onChangeCB?.('currentPlan', { tarifPlanId: newValue.tarifPlanId });
          }}
          id="currentPlan"
          options={optionsTariffPlans}
          isOptionEqualToValue={(option, value) => option.tarifPlanId === value.tarifPlanId}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => <TextField {...params} label="Тариф на текущий период" />}
        />
      ) : (
        <Typography variant="h6" component="div" sx={{ color: 'text.secondary', paddingTop: '20px' }}>
          {headerPrefix ? `${headerPrefix}: ` : ''}
          {currentTariffPlan?.name}
        </Typography>
      )}
      <Typography variant="body2" component="div" sx={{ maxWidth: onChangeCB ? null : '500px' }}>
        {currentTariffPlan?.description}
      </Typography>
      {onChangeCB ? (
        <DatePicker
          label="Дата следующего списания"
          value={formState.nextPaymentDate ? new Date(formState.nextPaymentDate) : null}
          onChange={handleChangeNextPaymentDate}
          renderInput={(params) => <TextField {...params} sx={{ width: '200px' }} size="small" />}
        />
      ) : (
        <Typography variant="subtitle2" component="div" sx={{ color: 'text.secondary' }}>
          Дата следующего списания:{' '}
          {companyAccount?.nextPlanActivateDate
            ? new Date(companyAccount?.nextPlanActivateDate).toLocaleDateString()
            : '–'}
        </Typography>
      )}
      <Box className={style.tariffControl}>
        <Autocomplete
          sx={{
            width: onChangeCB && putCompanyAccountParams ? '100%' : '400px',
          }}
          size="small"
          value={formState.selectedNextTariff}
          onChange={(_, newValue) => {
            handleFormChange('selectedNextTariff', newValue);
            onChangeCB?.('nextPlan', { tarifPlanId: newValue.tarifPlanId });
          }}
          id="nextPlan"
          options={optionsTariffPlans}
          isOptionEqualToValue={(option, value) => option.tarifPlanId === value.tarifPlanId}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => <TextField {...params} label="Тариф на следующий период" />}
        />
        {!onChangeCB && (
          <>
            <Button variant="text" disabled={!isChangeTariff} onClick={resetSelectedTariff}>
              Отмена
            </Button>
            <Button variant="contained" disabled={isErr || !isChangeTariff} onClick={updateTariff}>
              Сохранить
            </Button>
          </>
        )}
      </Box>
      {formState.selectedNextTariff ? (
        <Box className={style.selectedTariff}>
          <Typography variant="subtitle2" component="div" sx={{ color: 'text.secondary' }}>
            Описание:
          </Typography>
          <Typography variant="body2" component="div" sx={{ maxWidth: onChangeCB ? null : '500px' }}>
            {formState.selectedNextTariff.description}
          </Typography>
        </Box>
      ) : null}
      <Box sx={{ width: '640px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <DateWithCheckbox
          label="Приостановить биллинг до указанной даты"
          value={formState.stopBefore}
          onChange={(newValue) => {
            handleFormChange('stopBefore', newValue);
            onChangeCB?.('stopBefore', newValue);
          }}
        />
        <DateWithCheckbox
          label="Приостановить биллинг после указанной даты"
          value={formState.stopAfter}
          onChange={(newValue) => {
            handleFormChange('stopAfter', newValue);
            onChangeCB?.('stopAfter', newValue);
          }}
        />
      </Box>
      {isErr && (
        <Alert sx={{ width: 'max-content' }} severity="error">
          <AlertTitle>Внутренняя ошибка</AlertTitle>
          Нельзя получить платежный аккаунт компании
        </Alert>
      )}
    </Box>
  );
}

CompanyTariffPlanPanel.propTypes = {
  companyAccount: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.objectOf(PropTypes.any)]),
  ),
  putCompanyAccountParams: PropTypes.func,
  loading: PropTypes.bool,
  headerPrefix: PropTypes.string,
  onChangeCB: PropTypes.func,
};

CompanyTariffPlanPanel.defaultProps = {
  companyAccount: null,
  putCompanyAccountParams: null,
  loading: null,
  headerPrefix: '',
  onChangeCB: null,
};

export default CompanyTariffPlanPanel;
